/**
 * <colour-split-card data-colour="Navy">
 *
 * Rendered inside a product grid <li> by sections/main-collection.liquid when 2+ colour
 * filters are active and a product matches more than one of them. The section renders the
 * same product card once per matching colour; this element switches its card to that colour
 * using the theme's own swatch logic (same path as a shopper clicking the swatch), so the
 * image, link, price and quick add all update correctly.
 */
const normalise = (value) => (value || '').trim().toLowerCase();

class ColourSplitCard extends HTMLElement {
  connectedCallback() {
    const colour = this.dataset.colour;
    if (!colour) return;

    Promise.all([
      customElements.whenDefined('product-card'),
      customElements.whenDefined('swatches-variant-picker-component'),
    ]).then(() => requestAnimationFrame(() => this.#apply(colour)));
  }

  #apply(colour) {
    const card = this.closest('li')?.querySelector('product-card');
    const picker = card?.querySelector('swatches-variant-picker-component');
    if (!card || !picker) return;

    const target = normalise(colour);
    const input = Array.from(picker.querySelectorAll('input[type="radio"][data-variant-id]')).find(
      (el) => normalise(el.value) === target
    );

    // Missing swatch, or the server already rendered this colour: nothing to do.
    if (!input || input.checked) return;

    // 1. Swap the image and links right away so there's no flash of the wrong colour.
    try {
      const mediaId = input.dataset.optionMediaId;
      const slideshow = card.refs?.slideshow;
      const slides = slideshow?.refs?.slides || [];
      if (mediaId && slides.length) {
        for (const slide of slides) {
          if (slide.getAttribute('variant-image') == null) continue;
          slide.hidden = slide.getAttribute('slide-id') !== mediaId;
        }
        slideshow.select?.({ id: mediaId }, undefined, { animate: false });
      }

      const variantId = input.dataset.variantId;
      if (variantId) {
        card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', variantId);
          link.href = url.toString();
        });
      }
    } catch (error) {
      console.warn('[colour-split-card] quick swap failed', error);
    }

    // 2. Let the theme finish the job (price, availability, quick add) via its normal swatch change.
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('colour-split-card')) {
  customElements.define('colour-split-card', ColourSplitCard);
}